import React from 'react';
import styled from '@grebban/style-system-react';
import { useTranslation } from 'react-i18next';
import { useHeaderContext } from '@fogia/core-web/contexts/HeaderContext';
import { useHeaderNavigationContext } from '@fogia/core-web/contexts/HeaderNavigationContext';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import Link from '@fogia/core-web/components/Link';
import Button from 'components/Button';
import { Logotype } from 'assets/media/icons/Logotype';
import { Hamburger } from 'assets/media/icons/Hamburger';
import Cross from 'assets/media/icons/Cross';
import { SearchIcon } from 'assets/media/icons/SearchIcon';
import { validThemes, type ThemeName } from 'config/branding/themes';
import { DesktopNavMenu } from './navigation/DesktopNavMenu';
import { MenuOverlay } from './MenuOverlay';
import { SearchOverlay } from './SearchOverlay';
import { StoreFinderOverlay } from './StoreFinderOverlay';
import { type MainMenuItem, type SubmenuSection, type UtilityMenuItem } from './types';

/**
 * Header bar shown on all viewports. On mobile: hamburger + logo + utility nav.
 * On desktop: main nav + logo + utility nav. Menu overlay is shared and driven by overlay state.
 */

const HeaderBar = styled('header')`
    position: relative;
    z-index: 100;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    height: var(--header-height);
    padding: 10px;
    transition: background-color 0.25s ease-out;
`;

const LogoLink = styled(Link)`
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    color: inherit;
`;

const MenuButtonContent = styled('span')`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const DesktopRightMenuWrapper = styled(MenuButtonContent)`
    gap: 30px;
`;

export const HeaderComponent = () => {
    const { t } = useTranslation();
    const {
        headerState: { isTransparent },
    } = useHeaderContext();
    const { headerNavigationState, setHeaderNavigationState } = useHeaderNavigationContext();
    const isMobile = useBreakpoint({ to: 'desktop.sm' });

    const rawHeaderTheme =
        (useAppSelector(
            (state) => (state.page?.data?.settings as { headerTheme?: string } | undefined)?.headerTheme,
        ) as string | undefined) ?? 'light';
    const pageHeaderTheme: ThemeName = validThemes.includes(rawHeaderTheme) ? (rawHeaderTheme as ThemeName) : 'light';

    const mainMenu =
        (useAppSelector((state) => state.application?.header?.data?.headerNavigation?.mainMenu) as
            | MainMenuItem[]
            | undefined) ?? [];
    const utilityMenu =
        (useAppSelector((state) => state.application?.header?.data?.headerNavigation?.utilityMenu) as
            | UtilityMenuItem[]
            | undefined) ?? [];
    const organization = useAppSelector(
        (state) => state.application?.config?.siteOptions?.organizationSchema?.organization,
    ) as { email?: string; telephone?: string } | undefined;

    // Single object for all overlay/navigation UI state: which menu is open, which submenu is active, which side to anchor.
    const overlayState = {
        desktopMenuActive: !!headerNavigationState.desktopMenuActive,
        mobileMenuActive: !!headerNavigationState.mobileMenuActive,
        activeSubMenu: headerNavigationState.activeSubMenu ?? '',
        anchor: headerNavigationState.desktopMenuAnchor ?? 'left',
        searchActive: !!headerNavigationState.searchActive,
        storeFinderActive: !!headerNavigationState.storeFinderActive,
    };

    const isStoreFinderOpen = overlayState.storeFinderActive;

    const activeMainItem = mainMenu?.find(
        (item) => (item.label ?? item.link?.text ?? '') === overlayState.activeSubMenu,
    );

    const activeUtilityItem = utilityMenu?.find(
        (item) => (item.label ?? item.link?.text ?? '') === overlayState.activeSubMenu,
    );

    const activeItem = activeMainItem ?? activeUtilityItem;

    // Use active item’s submenu only when it’s an array of sections (CMS can send other shapes; we ignore those).
    const submenuSections: SubmenuSection[] | undefined = (() => {
        const raw = activeItem && 'content' in activeItem ? activeItem.content?.submenu : undefined;
        return Array.isArray(raw) ? (raw as SubmenuSection[]) : undefined;
    })();

    // When a top-level item is both a link and has a submenu, show that link first: either prepend it to the first section's items (if that section has no title) or add it as its own section.
    const sectionsToRender: SubmenuSection[] = (() => {
        const sections = submenuSections ?? [];
        const link = activeItem?.link;
        const label = activeItem?.label ?? link?.text ?? '';
        if (!sections.length || !link?.url || label === '') return sections;
        const parentLinkItem = { key: 'parent-link', label, link: { text: label, url: link.url } };
        const first = sections[0];
        const firstHasNoTitle = first && (first.title == null || first.title === '');
        if (firstHasNoTitle) {
            return [{ ...first, items: [parentLinkItem, ...first.items] }, ...sections.slice(1)];
        }
        return [{ columnLayout: '1', items: [parentLinkItem] }, ...sections];
    })();

    // Close overlay and clear any active submenu (backdrop click, escape, or link click in overlay).
    const handleCloseOverlay = () => {
        setHeaderNavigationState({
            ...headerNavigationState,
            activeSubMenu: '',
            desktopMenuActive: false,
            mobileMenuActive: false,
            parentMenuName: null,
            searchActive: false,
        });
    };

    // Toggle mobile overlay open/closed when user taps hamburger or “Menu”.
    const handleMobileMenuClick = () => {
        setHeaderNavigationState({
            ...headerNavigationState,
            mobileMenuActive: !overlayState.mobileMenuActive,
            activeSubMenu: '',
            parentMenuName: null,
            searchActive: false,
        });
    };

    // Toggle search overlay open/closed.
    const handleSearchClick = () => {
        setHeaderNavigationState({
            ...headerNavigationState,
            searchActive: !overlayState.searchActive,
            mobileMenuActive: false,
            desktopMenuActive: false,
            activeSubMenu: '',
            parentMenuName: null,
        });
    };

    // Close search overlay.
    const handleCloseSearch = () => {
        setHeaderNavigationState({
            ...headerNavigationState,
            searchActive: false,
        });
    };

    // Toggle store finder overlay open/closed.
    const handleStoreFinderClick = () => {
        setHeaderNavigationState({
            ...headerNavigationState,
            storeFinderActive: !isStoreFinderOpen,
            mobileMenuActive: false,
            desktopMenuActive: false,
            activeSubMenu: '',
            parentMenuName: null,
            searchActive: false,
        });
    };

    // Close store finder overlay.
    const handleCloseStoreFinder = () => {
        setHeaderNavigationState({
            ...headerNavigationState,
            storeFinderActive: false,
        });
    };

    // When user taps a main menu item that has a submenu, we store its label so the overlay shows that submenu.
    const handleSetActiveSubMenu = (label: string) => {
        setHeaderNavigationState({
            ...headerNavigationState,
            activeSubMenu: label,
        });
    };

    // Overlay is “open” when either mobile or desktop menu is active; we only show one at a time based on viewport.
    const isOverlayOpen = isMobile ? overlayState.mobileMenuActive : overlayState.desktopMenuActive;
    const isDesktopOverlayOpen = !isMobile && isOverlayOpen;

    const isScrolledUp = !isTransparent;

    // Header bar theme (text/link/icon colors).
    let mobileHeaderTheme = pageHeaderTheme;
    if (isOverlayOpen) {
        mobileHeaderTheme = 'orange';
    } else if (isScrolledUp) {
        mobileHeaderTheme = 'light';
    }

    let desktopHeaderTheme: ThemeName = pageHeaderTheme;
    if (isDesktopOverlayOpen || isScrolledUp) {
        desktopHeaderTheme = 'light';
    }

    const headerBarTheme: ThemeName = isMobile ? mobileHeaderTheme : desktopHeaderTheme;

    // Header bar background. Transparent when in top or overlay open, solid when scrolled up.
    const showBackground = isScrolledUp && !isOverlayOpen;
    const headerBarBackground = showBackground ? 'var(--ui-background)' : 'transparent';

    // Desktop nav theme per side: when overlay open, the menu that opened it gets orange; the other gets 'light'. When overlay closed, use headerBarTheme.
    const getDesktopNavTheme = (placement: 'left' | 'right'): ThemeName =>
        isDesktopOverlayOpen && overlayState.anchor === placement ? 'orange' : headerBarTheme;

    return (
        <>
            <HeaderBar data-theme={headerBarTheme} backgroundColor={headerBarBackground}>
                {/* Mobile: hamburger (or cross when open) on the left. Desktop: main nav on the left. */}
                {isMobile ? (
                    <Button
                        theme="textButton"
                        typography="Primary/Mobile/13_100_510_-1"
                        type="button"
                        onClick={overlayState.mobileMenuActive ? handleCloseOverlay : handleMobileMenuClick}
                        aria-expanded={overlayState.mobileMenuActive}
                        aria-haspopup="dialog"
                        aria-controls="header-menu-overlay"
                        aria-label={overlayState.mobileMenuActive ? t('header.close') : t('header.menu')}
                    >
                        <MenuButtonContent
                            color={overlayState.mobileMenuActive ? 'var(--button-tertiery-text-active)' : ''}
                        >
                            {overlayState.mobileMenuActive ? (
                                <Cross color="currentColor" width="15px" height="15px" size="medium" />
                            ) : (
                                <Hamburger color="currentColor" width="15px" height="15px" />
                            )}
                            {overlayState.mobileMenuActive ? t('header.close') : t('header.menu')}
                        </MenuButtonContent>
                    </Button>
                ) : (
                    <DesktopNavMenu
                        items={mainMenu}
                        ariaLabel={t('header.main_menu')}
                        placement="left"
                        dataTheme={getDesktopNavTheme('left')}
                    />
                )}
                {/* Centred logo; absolute so it doesn’t affect flex layout. */}
                <LogoLink to="/" aria-label={t('header.to_startpage')}>
                    <Logotype color="var(--ui-primary)" height={['30px', null, null, null, null, '40px']} aria-hidden />
                </LogoLink>
                {/* Mobile: stores + search buttons. Desktop: utility menu + search button on the right. */}
                {isMobile ? (
                    <MenuButtonContent gap="16px">
                        <Button
                            theme="textButton"
                            typography="Primary/Mobile/13_100_510_-1"
                            type="button"
                            onClick={handleStoreFinderClick}
                            aria-expanded={isStoreFinderOpen}
                            aria-haspopup="dialog"
                        >
                            {t('store_finder.stores', 'Stores')}
                        </Button>
                        <Button
                            theme="textButton"
                            typography="Primary/Mobile/13_100_510_-1"
                            type="button"
                            onClick={handleSearchClick}
                            aria-expanded={overlayState.searchActive}
                            aria-haspopup="dialog"
                            aria-label={t('header.search_label', 'Search')}
                        >
                            <MenuButtonContent>
                                <SearchIcon color="currentColor" width="15px" height="15px" />
                                {t('header.search_label', 'Search')}
                            </MenuButtonContent>
                        </Button>
                    </MenuButtonContent>
                ) : (
                    <DesktopRightMenuWrapper>
                        <Button
                            theme="textButton"
                            typography="Primary/15_120_510_-1"
                            type="button"
                            onClick={handleSearchClick}
                            aria-expanded={overlayState.searchActive}
                            aria-haspopup="dialog"
                            aria-label={t('header.search_label', 'Search')}
                        >
                            <MenuButtonContent>
                                <SearchIcon color="currentColor" width="15px" height="15px" />
                                {t('header.search_label', 'Search')}
                            </MenuButtonContent>
                        </Button>
                        <DesktopNavMenu
                            items={utilityMenu}
                            ariaLabel={t('header.utility_menu')}
                            placement="right"
                            dataTheme={getDesktopNavTheme('right')}
                        />

                        <Button
                            theme="textButton"
                            typography="Primary/15_120_510_-1"
                            type="button"
                            onClick={handleStoreFinderClick}
                            aria-expanded={isStoreFinderOpen}
                            aria-haspopup="dialog"
                            tabIndex={isStoreFinderOpen ? -1 : undefined}
                            aria-hidden={isStoreFinderOpen}
                            opacity={isStoreFinderOpen ? 0 : 1}
                            pointerEvents={isStoreFinderOpen ? 'none' : 'auto'}
                        >
                            {t('store_finder.title', 'Store finder')}
                        </Button>
                    </DesktopRightMenuWrapper>
                )}
            </HeaderBar>
            {/* Full-screen overlay: shows submenu (desktop) or full nav + submenu (mobile) when a menu is open. */}
            <MenuOverlay
                id="header-menu-overlay"
                isOpen={isOverlayOpen}
                variant={isMobile ? 'mobile' : 'desktop'}
                anchor={overlayState.anchor}
                activeSubMenu={overlayState.activeSubMenu}
                submenuSections={isMobile ? (overlayState.activeSubMenu ? sectionsToRender : []) : sectionsToRender}
                mobileMenuItems={isMobile ? [...(mainMenu ?? []), ...(utilityMenu ?? [])] : null}
                onSetActiveSubMenu={isMobile ? handleSetActiveSubMenu : () => {}}
                onCloseOverlay={handleCloseOverlay}
                contactEmail={organization?.email}
                contactPhone={organization?.telephone}
            />
            {/* Search overlay: right-side panel (desktop) or full-screen (mobile). */}
            <SearchOverlay isOpen={overlayState.searchActive} onClose={handleCloseSearch} />
            {/* Store finder overlay: full-screen with map + store list. */}
            <StoreFinderOverlay isOpen={isStoreFinderOpen} onClose={handleCloseStoreFinder} />
        </>
    );
};
